import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { Save, Pencil, Phone } from "lucide-react";
import { VEHICLE_LABEL, VEHICLE_TYPES } from "../lib/pricing";
import CityAutocomplete from "./CityAutocomplete";

const emptyForm = {
  vehicle_type: "van", vehicle_plate: "", vehicle_brand: "",
  max_weight_kg: "", max_volume_m3: "", vehicle_ptac_kg: "",
  base_city: "", base_postal_code: "", base_lat: "", base_lng: "", radius_km: 50,
  bio: "", contact_phone: "",
};

export default function TransporterProfileCard({ profile, onSaved, startEditing = false }) {
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(!profile || startEditing);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (profile) {
      setForm({
        vehicle_type: profile.vehicle_type || "van",
        vehicle_plate: profile.vehicle_plate || "",
        vehicle_brand: profile.vehicle_brand || "",
        vehicle_ptac_kg: profile.vehicle_ptac_kg || "",
        max_weight_kg: profile.max_weight_kg || "",
        max_volume_m3: profile.max_volume_m3 || "",
        base_city: profile.base_city || "",
        base_postal_code: profile.base_postal_code || "",
        base_lat: profile.base_lat ?? "",
        base_lng: profile.base_lng ?? "",
        radius_km: profile.radius_km || 50,
        bio: profile.bio || "",
        contact_phone: profile.contact_phone || "",
      });
    }
  }, [profile]);

  const up = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.base_lat || !form.base_lng) {
      toast.error("Sélectionnez votre ville dans la liste de suggestions pour valider sa localisation GPS.");
      return;
    }
    setSaving(true);
    try {
      await api.put("/transporter/profile", {
        ...form,
        vehicle_ptac_kg: form.vehicle_ptac_kg ? Number(form.vehicle_ptac_kg) : null,
        max_weight_kg: form.max_weight_kg ? parseFloat(form.max_weight_kg) : null,
        max_volume_m3: form.max_volume_m3 ? parseFloat(form.max_volume_m3) : null,
        base_lat: parseFloat(form.base_lat),
        base_lng: parseFloat(form.base_lng),
        radius_km: parseFloat(form.radius_km),
      });
      toast.success("Profil enregistré");
      setEditing(false);
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="border border-[#1F4032]/10 bg-white" data-testid="transporter-profile">
      <div className="flex justify-between items-center p-6 pb-3">
        <h2 className="font-display font-bold text-xl tracking-tight">Mon profil transporteur</h2>
        {profile && !editing && (
          <Button variant="ghost" onClick={() => setEditing(true)} className="rounded-md gap-2" data-testid="profile-edit-btn">
            <Pencil className="w-4 h-4" /> Modifier
          </Button>
        )}
      </div>
      <div className="px-6 pb-6">
        {!editing && profile ? (
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><div className="overline text-muted-foreground">Véhicule</div>{VEHICLE_LABEL[profile.vehicle_type] || profile.vehicle_type}</div>
            <div><div className="overline text-muted-foreground">Plaque</div>{profile.vehicle_plate || "—"}</div>
            <div>
              <div className="overline text-muted-foreground">Zone base</div>
              {profile.base_city || "—"}
              {profile.base_postal_code && <span className="ml-1 text-xs font-mono text-neutral-500">({profile.base_postal_code})</span>}
            </div>
            <div><div className="overline text-muted-foreground">Rayon</div>{profile.radius_km} km</div>
            <div className="sm:col-span-2 md:col-span-4">
              <div className="overline text-muted-foreground">Téléphone de contact</div>
              {profile.contact_phone ? (
                <a href={`tel:${profile.contact_phone}`} className="text-[#1F4032] hover:underline flex items-center gap-1.5 mt-0.5">
                  <Phone className="w-3.5 h-3.5" /> {profile.contact_phone}
                </a>
              ) : (
                <span className="text-neutral-400">Non renseigné</span>
              )}
            </div>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label className="overline">Type véhicule</Label>
              <select value={form.vehicle_type} onChange={up("vehicle_type")}
                className="w-full rounded-md mt-1 h-11 border border-input px-3 bg-background"
                data-testid="profile-vehicle-type">
                {VEHICLE_TYPES.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.label} (jusqu'à {v.max_weight_kg >= 1000 ? `${v.max_weight_kg / 1000} t` : `${v.max_weight_kg} kg`})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label className="overline">Plaque</Label>
              <Input value={form.vehicle_plate} onChange={up("vehicle_plate")}
                className="rounded-md mt-1 h-11" data-testid="profile-plate" />
            </div>
            <div>
              <Label className="overline">Marque/Modèle</Label>
              <Input value={form.vehicle_brand} onChange={up("vehicle_brand")}
                className="rounded-md mt-1 h-11" />
            </div>
            <div className="md:col-span-2">
              <CityAutocomplete
                value={{
                  city: form.base_city,
                  postal_code: form.base_postal_code,
                  lat: form.base_lat,
                  lng: form.base_lng,
                }}
                onChange={({ city, postal_code, lat, lng }) =>
                  setForm((f) => ({
                    ...f,
                    base_city: city,
                    base_postal_code: postal_code,
                    base_lat: lat ?? "",
                    base_lng: lng ?? "",
                  }))
                }
                testId="profile-base-city"
              />
            </div>
            <div>
              <Label className="overline">Rayon (km)</Label>
              <Input type="number" value={form.radius_km} onChange={up("radius_km")}
                className="rounded-md mt-1 h-11" data-testid="profile-radius" />
            </div>
            <div>
              <Label className="overline">PTAC du véhicule (kg, carte grise)</Label>
              <Input type="number" required min="1" max="19000" value={form.vehicle_ptac_kg} onChange={up("vehicle_ptac_kg")}
                className="rounded-md mt-1 h-11" data-testid="profile-ptac" />
              <p className="text-xs text-neutral-500">PTAC du véhicule ≤ 19 000 kg ; marchandise ≤ 6 000 kg par envoi. La charge utile est vérifiée sur la carte grise.</p>
            </div>
            <div>
              <Label className="overline">Charge max (kg)</Label>
              <Input type="number" value={form.max_weight_kg} onChange={up("max_weight_kg")}
                className="rounded-md mt-1 h-11" />
            </div>
            <div>
              <Label className="overline">Volume max (m³)</Label>
              <Input type="number" value={form.max_volume_m3} onChange={up("max_volume_m3")}
                className="rounded-md mt-1 h-11" />
            </div>
            <div className="md:col-span-3">
              <Label className="overline">Téléphone de contact (visible par les expéditeurs)</Label>
              <div className="relative mt-1">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                <Input
                  type="tel"
                  value={form.contact_phone}
                  onChange={up("contact_phone")}
                  placeholder="06 12 34 56 78"
                  className="rounded-md h-11 pl-9"
                  data-testid="profile-contact-phone"
                />
              </div>
              <p className="text-xs text-neutral-500 mt-1">
                Ce numéro sera affiché à l'expéditeur après acceptation d'une mission pour faciliter la coordination.
              </p>
            </div>
            <div className="md:col-span-3">
              <Label className="overline">Bio</Label>
              <Textarea value={form.bio} onChange={up("bio")} rows={2}
                className="rounded-md mt-1" />
            </div>

            <div className="md:col-span-3 flex gap-2 pt-2">
              <Button onClick={save} disabled={saving} className="rounded-full bg-[#1F4032] text-white hover:bg-[#2A5443] gap-2 px-6"
                data-testid="profile-save">
                <Save className="w-4 h-4" /> {saving ? "Enregistrement…" : "Enregistrer mon profil"}
              </Button>
              {profile && (
                <Button variant="outline" onClick={() => setEditing(false)}
                  className="rounded-full border-neutral-300">Annuler</Button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
